<template>
  <div
    v-if="estimate"
    class="estimate-pdf-page"
    data-estimate-pdf
    data-estimate-pdf-ready="true"
  >
    <div v-if="isDraft" class="draft-watermark">DRAFT</div>

    <div class="document-meta">
      <div class="recipient-block">
        <div class="recipient-name">{{ recipientName }}</div>
        <div v-if="estimate.recipient?.postal_code">〒{{ estimate.recipient.postal_code }}</div>
        <div>{{ estimate.recipient?.address || '' }}</div>
      </div>
      <div class="issuer-block">
        <div>{{ formatJapaneseDate(estimate.issue_date) }}</div>
        <div>見積番号 {{ estimate.estimate_no }}</div>
        <div v-if="Number(estimate.revision_no) > 0">{{ formatRevisionLabel(estimate.revision_no) }}</div>
      </div>
    </div>

    <h1>御 見 積 書</h1>

    <div class="intro-row">
      <p>下記の通り御見積申し上げます。</p>
      <div
        :class="[
          'company-block',
          { 'company-block--with-seal': Boolean(estimate.seal_flg) }
        ]"
      >
        <div class="company-name">株式会社 創英企画</div>
        <div>〒151-0053 東京都渋谷区代々木2-38-6</div>
        <div>TEL 03-3370-8006 / FAX 03-3370-8157</div>
        <div>登録番号 T7011001012662</div>
        <div v-if="estimate.created_by_user?.name">担当：{{ estimate.created_by_user.name }}</div>
        <CompanySeal :visible="Boolean(estimate.seal_flg)" class="company-seal" />
      </div>
    </div>

    <div class="total-box">
      <span>御見積合計金額</span>
      <strong>{{ currency(estimate.total_amount) }}－</strong>
      <small>（消費税込）</small>
    </div>

    <table class="condition-table">
      <tbody>
        <tr>
          <th>件名</th>
          <td>{{ estimate.title }}</td>
        </tr>
        <tr>
          <th>作業場所</th>
          <td>{{ workLocation }}</td>
        </tr>
        <tr>
          <th>見積有効期限</th>
          <td>{{ estimate.valid_until ? formatJapaneseDate(estimate.valid_until) : '－' }}</td>
        </tr>
      </tbody>
    </table>

    <table class="detail-table">
      <thead>
        <tr>
          <th class="item-column">項目</th>
          <th class="spec-column">仕様・摘要</th>
          <th class="quantity-column">数量</th>
          <th class="unit-column">単位</th>
          <th class="price-column">単価</th>
          <th class="amount-column">金額</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="row in displayRows" :key="row.key">
          <tr v-if="row.kind === 'section'" class="section-row">
            <td colspan="6">{{ row.name }}</td>
          </tr>
          <tr v-else :class="[`line-${row.line.line_type}`, 'estimate-detail-row']">
            <td>{{ lineName(row.line) }}</td>
            <td class="multiline">{{ lineSpecification(row.line) }}</td>
            <td class="number-cell">{{ quantity(row.line) }}</td>
            <td class="center-cell">{{ row.line.unit_nm || '' }}</td>
            <td class="number-cell">{{ unitPrice(row.line) }}</td>
            <td class="number-cell">{{ lineAmount(row.line) }}</td>
          </tr>
        </template>
        <tr v-if="displayRows.length === 0">
          <td colspan="6" class="empty-row">明細はありません。</td>
        </tr>
      </tbody>
    </table>

    <div class="summary-wrapper">
      <table class="summary-table">
        <tbody>
          <tr>
            <th>小計</th>
            <td>{{ currency(estimate.subtotal_amount) }}</td>
          </tr>
          <tr v-if="Number(estimate.discount_amount || 0) !== 0">
            <th>値引</th>
            <td>-{{ currency(estimate.discount_amount) }}</td>
          </tr>
          <tr>
            <th>消費税</th>
            <td>{{ currency(estimate.tax_amount) }}</td>
          </tr>
          <tr class="grand-total-row">
            <th>合計</th>
            <td>{{ currency(estimate.total_amount) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="estimate.general_notes" class="notes-block">
      <div v-if="estimate.general_notes">
        <strong>備考：</strong>
        <span>{{ estimate.general_notes }}</span>
      </div>
    </div>

    <div v-if="isDraft" class="draft-note">
      ※本書は承認前の下書きです。
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import CompanySeal from '@/components/shared/CompanySeal.vue';
import { formatEstimateQuantity } from '@/utils/estimateEditor';
import { formatRevisionLabel } from '@/utils/estimates';
import { formatJapaneseGregorianDate } from '@/utils/estimatePdfDates';

const props = defineProps({
  estimate: {
    type: Object,
    required: true
  }
});

const isDraft = computed(() => props.estimate?.approval_status !== 'approved');

const recipientName = computed(() => {
  const recipient = props.estimate?.recipient || {};
  const honorific = recipient.honorific || '御中';
  return [
    recipient.customer_nm,
    recipient.department_nm,
    recipient.person_nm,
    honorific
  ].filter(Boolean).join(' ');
});

const workLocation = computed(() =>
  props.estimate?.work_location ||
  [
    props.estimate?.building_snapshot?.building_nm,
    props.estimate?.building_snapshot?.address
  ].filter(Boolean).join('　') ||
  '－'
);

const displayRows = computed(() => {
  const rows = [];
  (props.estimate?.sections || []).forEach(section => {
    rows.push({
      key: `section-${section.id}`,
      kind: 'section',
      name: section.section_nm
    });
    (section.lines || []).forEach(line => {
      rows.push({
        key: `line-${line.id || `${section.id}-${line.sort_no}`}`,
        kind: 'line',
        line
      });
    });
  });
  (props.estimate?.unsectioned_lines || []).forEach(line => {
    rows.push({
      key: `unsectioned-line-${line.id || line.sort_no}`,
      kind: 'line',
      line
    });
  });
  return rows;
});

const currency = value =>
  `￥${Math.round(Number(value || 0)).toLocaleString('ja-JP')}`;

const formatJapaneseDate = formatJapaneseGregorianDate;

const lineName = line => {
  if (line.line_type === 'subtotal') return line.item_nm || '小計';
  if (line.line_type === 'discount') return line.item_nm || '値引';
  return line.item_nm || line.notes || '';
};

const lineSpecification = line =>
  [line.part_nm, line.specification, line.notes].filter(Boolean).join('\n');

const quantity = line => {
  if (['note', 'subtotal'].includes(line.line_type) || line.quantity == null) return '';
  return formatEstimateQuantity(line.quantity, 'ja-JP', line.quantity_decimal_places);
};

const unitPrice = line => {
  if (['note', 'subtotal'].includes(line.line_type) || line.unit_price == null) return '';
  return Math.round(Number(line.unit_price)).toLocaleString('ja-JP');
};

const lineAmount = line => {
  if (line.line_type === 'note') return '';
  const amount = Math.round(Number(line.amount || 0)).toLocaleString('ja-JP');
  return line.line_type === 'discount' ? `-${amount}` : amount;
};
</script>

<style scoped>
.estimate-pdf-page {
  --pdf-rule: 1px solid #666;
  --pdf-rule-dark: 1px solid #555;
  position: relative;
  box-sizing: border-box;
  width: 210mm;
  min-height: 297mm;
  padding: 11mm 13mm 10mm;
  overflow: hidden;
  color: #111;
  background: #fff;
  font-family: "Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", "MS Mincho", serif;
  font-size: 10pt;
  line-height: 1.45;
}

.draft-watermark {
  position: absolute;
  top: 122mm;
  left: 23mm;
  z-index: 0;
  color: rgba(180, 0, 0, 0.08);
  font-family: Arial, sans-serif;
  font-size: 68pt;
  font-weight: 700;
  letter-spacing: 8mm;
  transform: rotate(-28deg);
  pointer-events: none;
}

.estimate-pdf-page > *:not(.draft-watermark) {
  position: relative;
  z-index: 1;
}

.document-meta {
  display: flex;
  min-height: 30mm;
  justify-content: space-between;
  gap: 10mm;
}

.recipient-block {
  width: 105mm;
}

.recipient-name {
  margin-bottom: 2mm;
  padding-bottom: 1mm;
  border-bottom: var(--pdf-rule);
  font-size: 14pt;
}

.issuer-block {
  width: 58mm;
  text-align: right;
}

h1 {
  margin: 1mm 0 6mm;
  font-size: 24pt;
  font-weight: 500;
  letter-spacing: 5mm;
  text-align: center;
}

.intro-row {
  display: flex;
  min-height: 30mm;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8mm;
}

.intro-row > p {
  margin: 4mm 0 0;
  font-size: 11pt;
}

.company-block {
  position: relative;
  box-sizing: border-box;
  min-width: 76mm;
  font-size: 8.5pt;
  text-align: right;
}

.company-block--with-seal {
  padding-top: 17.5mm;
}

.company-seal {
  position: absolute;
  top: 0;
  right: 16mm;
}

.company-name {
  margin-bottom: 1mm;
  font-size: 13pt;
  font-weight: 700;
  letter-spacing: 0.7mm;
}

.total-box {
  display: flex;
  margin: 3mm 0 4mm;
  padding: 2.5mm 4mm;
  align-items: baseline;
  border: var(--pdf-rule);
  gap: 4mm;
}

.total-box span {
  font-size: 12pt;
}

.total-box strong {
  flex: 1;
  border-bottom: var(--pdf-rule);
  font-size: 18pt;
  font-weight: 400;
  text-align: right;
}

.total-box small {
  white-space: nowrap;
}

table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.condition-table {
  margin-bottom: 4mm;
}

.condition-table,
.detail-table,
.summary-table {
  border-top: var(--pdf-rule);
  border-left: var(--pdf-rule);
}

.condition-table th,
.condition-table td {
  padding: 1.2mm 2mm;
  border: 0;
  border-right: var(--pdf-rule);
  border-bottom: var(--pdf-rule);
}

.condition-table th {
  width: 28mm;
  background: #f2f2f2;
  font-weight: 400;
}

.detail-table th,
.detail-table td {
  height: 7mm;
  padding: 1mm 1.4mm;
  border: 0;
  border-right: var(--pdf-rule);
  border-bottom: var(--pdf-rule);
  vertical-align: top;
}

.detail-table thead th {
  background: #ececec;
  font-weight: 400;
  text-align: center;
}

.item-column {
  width: 37mm;
}

.spec-column {
  width: 70mm;
}

.quantity-column {
  width: 14mm;
}

.unit-column {
  width: 12mm;
}

.price-column,
.amount-column {
  width: 23mm;
}

.section-row td {
  height: auto;
  padding: 1.3mm 2mm;
  background: #e5e5e5;
  font-weight: 400;
}

.line-note td {
  color: #333;
  font-size: 8.5pt;
}

.line-subtotal td,
.line-discount td {
  font-weight: 400;
}

.multiline,
.notes-block span {
  white-space: pre-line;
}

.number-cell {
  white-space: nowrap;
  text-align: right;
}

.center-cell,
.empty-row {
  text-align: center;
}

.summary-wrapper {
  display: flex;
  justify-content: flex-end;
}

.summary-table {
  width: 69mm;
}

.summary-table th,
.summary-table td {
  padding: 1.2mm 2mm;
  border: 0;
  border-right: var(--pdf-rule);
  border-bottom: var(--pdf-rule);
}

.summary-table th {
  width: 25mm;
  background: #f2f2f2;
  font-weight: 400;
  text-align: right;
}

.summary-table td {
  text-align: right;
}

.grand-total-row th,
.grand-total-row td {
  font-size: 11pt;
  font-weight: 400;
}

.notes-block {
  margin-top: 4mm;
  padding: 2mm 3mm;
  border: var(--pdf-rule-dark);
  font-size: 8.5pt;
}

.notes-block > div + div {
  margin-top: 1mm;
}

.notes-block strong {
  font-weight: 400;
}

.draft-note {
  margin-top: 2mm;
  color: #a00;
  font-size: 8pt;
  text-align: right;
}
</style>
